import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Bell,
  ChevronDown,
  ChevronsLeft,
  ChevronsRight,
  ExternalLink,
  Loader2,
  LogOut,
  Menu,
  Search,
  X,
} from "lucide-react";
import AdminSidebar from "./AdminSidebar";
import CommandPalette from "./CommandPalette";
import { getPageMeta } from "./adminNav";
import AdminJobsTray from "../../components/admin/AdminJobsTray";
import NotificationBell from "../../components/admin/notifications/NotificationBell";
import {
  AdminNotificationsProvider,
  useAdminNotificationsContext,
} from "../../components/admin/notifications/AdminNotificationsContext";
import UserAvatar from "../../components/UserAvatar";
import { useAuth } from "../../contexts/AuthContext";
import { usePermission } from "../../hooks/usePermission";
import { API, apiFetch, cx, ADMIN_SESSION_EXPIRED_EVENT } from "./_shared";

const COLLAPSE_KEY = "adminSidebarCollapsed";
const IS_MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPad/i.test(navigator.platform || "");

function readCollapsed() {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === "1";
  } catch {
    return false;
  }
}

const iconBtn =
  "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-admin-textMuted transition hover:bg-admin-muted hover:text-admin-text";

function UserMenu({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const name = user?.fullName || user?.username || "Admin";
  const { roleLabel } = usePermission();

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex h-9 items-center gap-2 rounded-lg pl-1 pr-1.5 transition hover:bg-admin-muted sm:pr-2"
      >
        <UserAvatar
          url={user?.avatar}
          name={user?.fullName || user?.username || user?.email || "Admin"}
          sizeClass="h-7 w-7"
          textClass="text-[11px] font-semibold"
          roundedClass="rounded-full"
          className="shrink-0 bg-admin-primaryLight"
          imgClassName="object-cover"
        />
        <span className="hidden min-w-0 text-left leading-tight md:block">
          <span className="block max-w-[140px] truncate text-[13px] font-medium text-admin-text">{name}</span>
          <span className="block text-[10px] text-admin-textMuted">{roleLabel}</span>
        </span>
        <ChevronDown size={14} className={cx("hidden text-admin-textMuted transition md:block", open && "rotate-180")} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-2 w-64 overflow-hidden rounded-xl border border-admin-border bg-admin-surface shadow-soft-xl"
          style={{ animation: "admin-pop-in 0.15s ease-out" }}
        >
          <div className="flex items-center gap-3 border-b border-admin-border px-4 py-3">
            <UserAvatar
              url={user?.avatar}
              name={user?.fullName || user?.username || user?.email || "Admin"}
              sizeClass="h-10 w-10"
              textClass="text-sm font-semibold"
              roundedClass="rounded-full"
              className="shrink-0 bg-admin-primaryLight"
              imgClassName="object-cover"
            />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-admin-text">{name}</p>
              <p className="truncate text-xs text-admin-textMuted">{user?.email || user?.username || "Quản trị viên"}</p>
            </div>
          </div>
          <div className="p-1.5">
            <Link
              to="/admin/notifications"
              role="menuitem"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-admin-text transition hover:bg-admin-muted"
            >
              <Bell size={15} className="text-admin-textMuted" />
              Thông báo
            </Link>
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              role="menuitem"
              onClick={() => setOpen(false)}
              className="flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-admin-text transition hover:bg-admin-muted"
            >
              <ExternalLink size={15} className="text-admin-textMuted" />
              Xem website
            </a>
          </div>
          <div className="border-t border-admin-border p-1.5">
            <button
              type="button"
              role="menuitem"
              onClick={onLogout}
              className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-rose-600 transition hover:bg-rose-50"
            >
              <LogOut size={15} />
              Đăng xuất
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminShell() {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, token, hydrated, logout, updateUser } = useAuth();
  const { unread } = useAdminNotificationsContext();
  const { pathname } = useLocation();
  const page = useMemo(() => getPageMeta(pathname), [pathname]);

  const effectiveToken = useMemo(
    () => token || localStorage.getItem("authToken") || localStorage.getItem("adminToken") || "",
    [token]
  );

  const closePalette = useCallback(() => setPaletteOpen(false), []);

  // Làm mới vai trò/quyền từ server (đổi quyền có hiệu lực ngay, kể cả phiên đăng nhập cũ)
  const isAdminUser = user?.role === "admin";
  useEffect(() => {
    if (!hydrated || !isAdminUser) return undefined;
    let alive = true;
    apiFetch(`${API}/auth/me`)
      .then((me) => {
        if (!alive || !me) return;
        updateUser({ adminRole: me.adminRole, permissions: me.permissions, mustChangePassword: Boolean(me.mustChangePassword) });
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrated, isAdminUser]);

  // API trả 401 (token hết hạn / không hợp lệ) → đăng xuất và về trang đăng nhập kèm lời nhắn,
  // thay vì để từng trang kẹt ở "Token không hợp lệ hoặc đã hết hạn".
  const navigate = useNavigate();
  useEffect(() => {
    let handled = false;
    const onExpired = () => {
      if (handled) return;
      handled = true;
      try {
        localStorage.removeItem("adminToken");
      } catch {
        /* ignore */
      }
      logout();
      navigate("/login", { replace: true, state: { sessionExpired: true, from: pathname } });
    };
    window.addEventListener(ADMIN_SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(ADMIN_SESSION_EXPIRED_EVENT, onExpired);
  }, [logout, navigate, pathname]);

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, [collapsed]);

  // Đóng drawer mobile khi đổi trang
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  // Tiêu đề tab: "(3) Dashboard · Admin Lòng Vòng Đà Lạt"
  useEffect(() => {
    const prev = document.title;
    const badge = unread > 0 ? `(${unread > 99 ? "99+" : unread}) ` : "";
    document.title = `${badge}${page.label} · Admin Lòng Vòng Đà Lạt`;
    return () => {
      document.title = prev;
    };
  }, [page.label, unread]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (e) => e.key === "Escape" && setMobileOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  // Ctrl/⌘ + K mở bảng lệnh
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Viền + bóng cho header khi cuộn
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!hydrated) {
    return (
      <div className="admin-root flex min-h-screen items-center justify-center">
        <div className="flex items-center gap-3 rounded-xl border border-admin-border bg-admin-surface px-5 py-4 shadow-soft">
          <Loader2 size={20} className="animate-spin text-admin-primary" />
          <p className="text-sm font-medium text-admin-textMuted">Đang kiểm tra đăng nhập...</p>
        </div>
      </div>
    );
  }

  if (!effectiveToken || !user || user.role !== "admin") {
    return <Navigate to="/login" replace />;
  }

  // Đang dùng mật khẩu tạm → buộc đổi trước khi vào khu quản trị
  if (user.mustChangePassword) {
    return <Navigate to="/tai-khoan#doi-mat-khau" replace />;
  }

  return (
    <div className="admin-root flex min-h-screen">
      <a
        href="#admin-main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-3 focus:top-3 focus:z-[200] focus:rounded-lg focus:bg-admin-primary focus:px-3 focus:py-2 focus:text-sm focus:font-semibold focus:text-white"
      >
        Bỏ qua điều hướng
      </a>

      {/* Desktop sidebar */}
      <aside
        className={cx(
          "sticky top-0 z-30 hidden h-screen shrink-0 border-r border-slate-900 transition-[width] duration-200 lg:block",
          collapsed ? "w-[72px]" : "w-64"
        )}
      >
        <AdminSidebar collapsed={collapsed} />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true" aria-label="Menu quản trị">
          <button
            type="button"
            aria-label="Đóng menu"
            className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]"
            style={{ animation: "admin-fade-in 0.15s ease-out" }}
            onClick={() => setMobileOpen(false)}
          />
          <aside
            className="absolute inset-y-0 left-0 w-72 max-w-[85vw] shadow-soft-xl"
            style={{ animation: "admin-slide-in-left 0.2s ease-out" }}
          >
            <button
              type="button"
              onClick={() => setMobileOpen(false)}
              aria-label="Đóng menu"
              className="absolute right-2 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:bg-white/10 hover:text-white"
            >
              <X size={18} />
            </button>
            <AdminSidebar onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header
          className={cx(
            "sticky top-0 z-20 flex h-14 shrink-0 items-center gap-3 border-b bg-admin-surface/85 px-3 backdrop-blur-md transition-shadow sm:px-6",
            scrolled ? "border-admin-border shadow-soft-md" : "border-admin-border/70"
          )}
        >
          {/* Trái: điều hướng + breadcrumb */}
          <div className="flex min-w-0 items-center gap-1.5">
            <button type="button" onClick={() => setMobileOpen(true)} aria-label="Mở menu" className={cx(iconBtn, "lg:hidden")}>
              <Menu size={19} />
            </button>
            <button
              type="button"
              onClick={() => setCollapsed((c) => !c)}
              aria-label={collapsed ? "Mở rộng sidebar" : "Thu gọn sidebar"}
              title={collapsed ? "Mở rộng sidebar" : "Thu gọn sidebar"}
              className={cx(iconBtn, "hidden lg:flex")}
            >
              {collapsed ? <ChevronsRight size={18} /> : <ChevronsLeft size={18} />}
            </button>

            <nav aria-label="Breadcrumb" className="ml-1 min-w-0">
              {page.section ? (
                <p className="hidden truncate text-[11px] font-medium uppercase tracking-wider text-admin-textMuted sm:block">
                  {page.section}
                </p>
              ) : null}
              <p className="truncate text-sm font-semibold leading-tight text-admin-text sm:text-[15px]">{page.label}</p>
            </nav>
          </div>

          {/* Giữa: ô tìm nhanh */}
          <div className="hidden flex-1 justify-center md:flex">
            <button
              type="button"
              onClick={() => setPaletteOpen(true)}
              aria-label="Tìm trang hoặc thao tác"
              className="group flex h-9 w-full max-w-sm items-center gap-2.5 rounded-lg border border-admin-border bg-admin-muted/60 px-3 text-sm text-admin-textMuted transition hover:border-admin-textMuted/40 hover:bg-admin-surface"
            >
              <Search size={15} className="shrink-0" />
              <span className="flex-1 truncate text-left">Tìm trang, thao tác…</span>
              <kbd className="rounded-md border border-admin-border bg-admin-surface px-1.5 py-0.5 text-[10px] font-semibold text-admin-textMuted">
                {IS_MAC ? "⌘" : "Ctrl"} K
              </kbd>
            </button>
          </div>

          {/* Phải: công cụ */}
          <div className="ml-auto flex shrink-0 items-center gap-0.5 sm:gap-1 md:ml-0">
            <button
              type="button"
              onClick={() => setPaletteOpen(true)}
              aria-label="Tìm nhanh"
              className={cx(iconBtn, "md:hidden")}
            >
              <Search size={18} />
            </button>
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Xem website"
              title="Xem website"
              className={cx(iconBtn, "hidden sm:flex")}
            >
              <ExternalLink size={17} />
            </a>
            <AdminJobsTray variant="admin" size="sm" />
            <NotificationBell />
            <span className="mx-1 hidden h-5 w-px bg-admin-border sm:block" aria-hidden />
            <UserMenu user={user} onLogout={logout} />
          </div>
        </header>

        <main id="admin-main" className="mx-auto w-full max-w-[1600px] flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>

      <CommandPalette open={paletteOpen} onClose={closePalette} onLogout={logout} />
    </div>
  );
}

const AdminLayout = () => (
  <AdminNotificationsProvider>
    <AdminShell />
  </AdminNotificationsProvider>
);

export default AdminLayout;
